import { isEqual, debounce } from 'lodash';

import BackgroundVideo from '../../../resources/scripts/helpers/background-video';

class BrixGrid {
  static init(...parameters) {
    return new BrixGrid(...parameters);
  }

  constructor(block) {
    if (block) {
      this.block = block;
      this.setUpBackgroundVideo();
      this.setupBlockOrder();
    }
  }

  setUpBackgroundVideo() {
    const backgroundVideoWrapper = document.querySelector(`#${this.block.id} > .background > .video`);

    if (backgroundVideoWrapper) {
      BackgroundVideo.init(this.block);
    }
  }

  setupBlockOrder() {
    this.content = this.block.querySelector('.content');
    this.innerBlocks = [...this.content.children];

    this.activeOrder = 'original';

    if (this.innerBlocks) {
      this.originalOrder = this.innerBlocks.map((innerBlock, index) => index + 1);
      this.mobileOrder = this.getValidatedOrder(this.content.dataset.gridOrderMobile === '' ? null : this.content.dataset.gridOrderMobile);
      this.tabletOrder = this.getValidatedOrder(this.content.dataset.gridOrderTablet === '' ? null : this.content.dataset.gridOrderTablet);

      // No need to update order if orders are the same
      // or if both mobile and tablet order are missing
      if (
        (this.mobileOrder || this.tabletOrder)
        && (!isEqual(this.mobileOrder, this.originalOrder)
          || !isEqual(this.tabletOrder, this.originalOrder))
      ) {
        this.updateGridOrder();

        window.addEventListener('resize', debounce(() => {
          this.updateGridOrder();
        }), 300);
      }
    }
  }

  // Checks if values are correct
  getValidatedOrder(order) {
    let parsedOrder;
    if (typeof order === 'string') {
      parsedOrder = order.split(',').map((value) => parseInt(value, 10));
    } else {
      parsedOrder = order;
    }

    // Check if any values is undefined, Nan, too high or too low
    if (parsedOrder && parsedOrder.length) {
      const orderWithValidValues = this.getValidOrderValues(parsedOrder);
      const orderWithUniqueValues = this.getUniqueOrderValues(orderWithValidValues);
      const orderWithValidLength = this.getValidOrderLength(orderWithUniqueValues);
      return orderWithValidLength;
    }

    return null;
  }

  getValidOrderValues(order) {
    const orderWithValidValues = order;
    orderWithValidValues.forEach((value, index) => {
      if (!value || Number.isNaN(value) || value < 1) {
        orderWithValidValues[index] = 1;
      }

      if (value > this.innerBlocks.length) {
        orderWithValidValues[index] = this.innerBlocks.length;
      }
    });

    return orderWithValidValues;
  }

  getValidOrderLength(order) {
    if (order.length !== this.innerBlocks.length) {
      const maxVal = order.reduce((a, b) => Math.max(a, b));
      // If order length is too high - remove highest value
      if (order.length > this.innerBlocks.length) {
        const index = order.indexOf(maxVal);
        if (index !== -1) {
          order.splice(index, 1);
        }
      } else {
        // If order length is too low - add a higher value to end
        order.push(
          maxVal >= this.innerBlocks.length
            ? this.innerBlocks.length
            : this.innerBlocks.length + 1,
        );
      }

      this.getValidOrderLength(order);
    }

    return order;
  }

  getUniqueOrderValues(order, DOMindex = 1) {
    const orderWithUniqueValues = order;
    //  Does index exist?
    const foundDOMindex = order.find((element) => element === DOMindex);

    if (foundDOMindex) {
      // If index exists, are there duplicates?
      let foundIndex;
      for (let i = 0; i < order.length; i += 1) {
        if (foundIndex) {
          if (order[i] === DOMindex) {
            // Assign new value to duplicates
            orderWithUniqueValues[i] = DOMindex + 1;
          }
        } else if (order[i] === DOMindex) {
          foundIndex = order[i];
        }
      }

      if (order.length === DOMindex) {
        // Quit when reached last value
        return orderWithUniqueValues;
      }

      // Run again with updated order array as long as index is lower than length of order array
      return this.getUniqueOrderValues(orderWithUniqueValues, DOMindex + 1);
    }

    // If index doesn't exist – find first lowest number in order and assign it to index
    let lowestIndexFound = false;
    let searchIndex = DOMindex;
    while (!lowestIndexFound) {
      searchIndex += 1;
      // eslint-disable-next-line no-loop-func
      const lowestIndex = order.findIndex((value) => value === searchIndex);

      if (lowestIndex !== -1) {
        orderWithUniqueValues[lowestIndex] = DOMindex;
        lowestIndexFound = true;
      }
    }

    if (order.length === DOMindex) {
      return orderWithUniqueValues;
    }

    return this.getUniqueOrderValues(orderWithUniqueValues, DOMindex + 1);
  }

  updateGridOrder() {
    if (window.innerWidth < 576 && this.activeOrder !== 'mobile') {
      this.activeOrder = 'mobile';
      this.reorderInnerBlocks(this.mobileOrder ? this.mobileOrder : this.originalOrder);
    } else if (this.tabletOrder && window.innerWidth >= 576 && window.innerWidth <= 1024 && this.activeOrder !== 'tablet') {
      this.activeOrder = 'tablet';
      this.reorderInnerBlocks(this.tabletOrder ? this.tabletOrder : this.originalOrder);
    } else if (window.innerWidth > 1025 && this.activeOrder !== 'original') {
      this.activeOrder = 'original';
      this.reorderInnerBlocks(this.originalOrder);
    }
  }

  reorderInnerBlocks(order) {
    if (order) {
      //  Remove blocks
      this.innerBlocks.forEach((block) => { block.remove(); });

      // Add back blocks in new order
      order.forEach((index) => {
        if (this.innerBlocks[index - 1]) {
          this.content.append(this.innerBlocks[index - 1]);
        }
      });
    }
  }
}

// Register class
if (window.brix && window.brix.registerBlock) {
  window.brix.registerBlock('brix-grid', BrixGrid.init);
}
